import { useQuery } from "react-query";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "../services/firebase";
import { DashboardStats, TopicStats } from "../types";

// 日付ヘルパー関数（JST基準）

const getJstDate = (): Date => {
  const now = new Date();
  return new Date(now.getTime() + 9 * 60 * 60 * 1000);
};

const formatDateString = (jstDate: Date): string => {
  const year = jstDate.getUTCFullYear();
  const month = String(jstDate.getUTCMonth() + 1).padStart(2, "0");
  const day = String(jstDate.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getTodayDateString = (): string => formatDateString(getJstDate());

const getYesterdayDateString = (): string => {
  const jstDate = getJstDate();
  jstDate.setUTCDate(jstDate.getUTCDate() - 1);
  return formatDateString(jstDate);
};

const getCurrentMonthString = (): string => {
  const jstDate = getJstDate();
  const year = jstDate.getUTCFullYear();
  const month = String(jstDate.getUTCMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

const getLastMonthString = (): string => {
  const jstDate = getJstDate();
  let year = jstDate.getUTCFullYear();
  let month = jstDate.getUTCMonth(); // 0-11
  month--;
  if (month < 0) {
    month = 11;
    year--;
  }
  return `${year}-${String(month + 1).padStart(2, "0")}`;
};

/**
 * 今週の月曜日〜今日の日付範囲を取得（月曜始まり）
 */
const getThisWeekRange = (): { startDate: string; endDate: string; weekLabel: string } => {
  const jstDate = getJstDate();
  const dayOfWeek = jstDate.getUTCDay(); // 0=日, 1=月, ..., 6=土
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  const monday = new Date(jstDate);
  monday.setUTCDate(jstDate.getUTCDate() - daysFromMonday);

  const startDate = formatDateString(monday);
  const endDate = formatDateString(jstDate);
  const weekLabel = `${monday.getUTCMonth() + 1}/${monday.getUTCDate()}〜`;

  return { startDate, endDate, weekLabel };
};

/**
 * 先週の月曜日〜日曜日の日付範囲を取得（月曜始まり）
 */
const getLastWeekRange = (): { startDate: string; endDate: string; weekLabel: string } => {
  const jstDate = getJstDate();
  const dayOfWeek = jstDate.getUTCDay();
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  const thisMonday = new Date(jstDate);
  thisMonday.setUTCDate(jstDate.getUTCDate() - daysFromMonday);

  const lastMonday = new Date(thisMonday);
  lastMonday.setUTCDate(thisMonday.getUTCDate() - 7);

  const lastSunday = new Date(thisMonday);
  lastSunday.setUTCDate(thisMonday.getUTCDate() - 1);

  const startDate = formatDateString(lastMonday);
  const endDate = formatDateString(lastSunday);
  const weekLabel = `${lastMonday.getUTCMonth() + 1}/${lastMonday.getUTCDate()}〜${lastSunday.getUTCMonth() + 1}/${lastSunday.getUTCDate()}`;

  return { startDate, endDate, weekLabel };
};

/**
 * 日付範囲に含まれる年月リストを取得
 */
const getMonthsBetween = (startDate: string, endDate: string): string[] => {
  const months: string[] = [];
  const [startYear, startMonth] = startDate.split("-").map(Number);
  const [endYear, endMonth] = endDate.split("-").map(Number);

  let year = startYear;
  let month = startMonth;

  while (year < endYear || (year === endYear && month <= endMonth)) {
    months.push(`${year}-${String(month).padStart(2, "0")}`);
    month++;
    if (month > 12) {
      month = 1;
      year++;
    }
  }

  return months;
};

/**
 * 空の集計データを作成
 */
const createEmptyAggregation = () => ({
  totalResponses: 0,
  totalLaunches: 0,
  uniqueLaunches: 0,
  totalViews: 0,
  byGenre: {} as { [genre: string]: number },
  byDifficulty: {} as { [difficulty: number]: number },
  byTopic: {} as { [topicId: string]: TopicStats },
});

/**
 * 1日分のデータを集計データに加算
 */
const addDailyDataToAggregation = (
  aggregation: ReturnType<typeof createEmptyAggregation>,
  data: Record<string, unknown>
) => {
  aggregation.totalResponses += (data.totalResponses as number) || 0;
  aggregation.totalLaunches += (data.totalLaunches as number) || 0;
  aggregation.uniqueLaunches += (data.uniqueLaunches as number) || 0;
  aggregation.totalViews += (data.totalViews as number) || 0;

  Object.entries((data.byGenre as Record<string, number>) || {}).forEach(([genre, count]) => {
    aggregation.byGenre[genre] = (aggregation.byGenre[genre] || 0) + count;
  });

  Object.entries((data.byDifficulty as Record<string, number>) || {}).forEach(
    ([difficulty, count]) => {
      aggregation.byDifficulty[Number(difficulty)] =
        (aggregation.byDifficulty[Number(difficulty)] || 0) + count;
    }
  );

  Object.entries((data.byTopic as Record<string, TopicStats>) || {}).forEach(([topicId, stats]) => {
    if (!aggregation.byTopic[topicId]) {
      aggregation.byTopic[topicId] = {
        count: 0,
        viewCount: 0,
        genre: stats.genre,
        difficulty: stats.difficulty,
        contentPreview: stats.contentPreview,
      };
    }
    aggregation.byTopic[topicId].count += stats.count;
    aggregation.byTopic[topicId].viewCount += stats.viewCount || 0;
  });
};

/**
 * Firestoreから月の全データを集計
 */
const aggregateMonthData = async (monthStr: string) => {
  const aggregation = createEmptyAggregation();

  try {
    const monthSnapshot = await getDocs(collection(db, "stats", "daily", monthStr));
    monthSnapshot.forEach((docSnap) => {
      addDailyDataToAggregation(aggregation, docSnap.data());
    });
  } catch (error) {
    console.error(`Error fetching month data for ${monthStr}:`, error);
  }

  return aggregation;
};

/**
 * Firestoreから日付範囲のデータを集計（週単位用）
 * 月をまたぐ場合にも対応
 */
const aggregateDateRangeData = async (startDate: string, endDate: string) => {
  const aggregation = createEmptyAggregation();
  const months = getMonthsBetween(startDate, endDate);

  try {
    for (const monthStr of months) {
      const monthSnapshot = await getDocs(collection(db, "stats", "daily", monthStr));
      monthSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const docDate = data.date as string;
        // 日付範囲内のみ集計
        if (docDate >= startDate && docDate <= endDate) {
          addDailyDataToAggregation(aggregation, data);
        }
      });
    }
  } catch (error) {
    console.error(`Error fetching date range data for ${startDate} - ${endDate}:`, error);
  }

  return aggregation;
};

/**
 * Firestore からダッシュボード統計を直接取得
 */
const fetchDashboardStatsFromFirestore = async (): Promise<DashboardStats> => {
  const today = getTodayDateString();
  const yesterday = getYesterdayDateString();
  const thisMonth = getCurrentMonthString();
  const lastMonth = getLastMonthString();
  const thisWeekRange = getThisWeekRange();
  const lastWeekRange = getLastWeekRange();

  const emptyData = {
    totalResponses: 0,
    totalLaunches: 0,
    uniqueLaunches: 0,
    totalViews: 0,
    byGenre: {},
    byDifficulty: {},
    byTopic: {},
  };

  // 今日のデータを取得
  const todayDocRef = doc(db, "stats", "daily", thisMonth, today);
  const todayDoc = await getDoc(todayDocRef);
  const todayData = todayDoc.exists() && todayDoc.data() ? todayDoc.data() : { ...emptyData };

  // 昨日のデータを取得（先月の場合を考慮）
  const yesterdayMonth = yesterday.substring(0, 7);
  const yesterdayDocRef = doc(db, "stats", "daily", yesterdayMonth, yesterday);
  const yesterdayDoc = await getDoc(yesterdayDocRef);
  const yesterdayData =
    yesterdayDoc.exists() && yesterdayDoc.data() ? yesterdayDoc.data() : { ...emptyData };

  // 週・月の集計を並列実行
  const [thisWeekData, lastWeekData, monthData, lastMonthData] = await Promise.all([
    aggregateDateRangeData(thisWeekRange.startDate, thisWeekRange.endDate),
    aggregateDateRangeData(lastWeekRange.startDate, lastWeekRange.endDate),
    aggregateMonthData(thisMonth),
    aggregateMonthData(lastMonth),
  ]);

  return {
    today: {
      date: today,
      totalResponses: todayData.totalResponses || 0,
      totalLaunches: todayData.totalLaunches || 0,
      uniqueLaunches: todayData.uniqueLaunches || 0,
      totalViews: todayData.totalViews || 0,
      byGenre: todayData.byGenre || {},
      byDifficulty: todayData.byDifficulty || {},
      byTopic: todayData.byTopic || {},
    },
    yesterday: {
      date: yesterday,
      totalResponses: yesterdayData.totalResponses || 0,
      totalLaunches: yesterdayData.totalLaunches || 0,
      uniqueLaunches: yesterdayData.uniqueLaunches || 0,
      totalViews: yesterdayData.totalViews || 0,
      byGenre: yesterdayData.byGenre || {},
      byDifficulty: yesterdayData.byDifficulty || {},
      byTopic: yesterdayData.byTopic || {},
    },
    thisWeek: {
      weekLabel: thisWeekRange.weekLabel,
      startDate: thisWeekRange.startDate,
      endDate: thisWeekRange.endDate,
      ...thisWeekData,
    },
    lastWeek: {
      weekLabel: lastWeekRange.weekLabel,
      startDate: lastWeekRange.startDate,
      endDate: lastWeekRange.endDate,
      ...lastWeekData,
    },
    thisMonth: {
      month: thisMonth,
      ...monthData,
    },
    lastMonth: {
      month: lastMonth,
      ...lastMonthData,
    },
    lastUpdated: new Date().toISOString(),
  };
};

/**
 * ダッシュボード統計取得Hook
 */
export const useDashboardStats = () => {
  return useQuery<DashboardStats, Error>("dashboardStats", fetchDashboardStatsFromFirestore, {
    staleTime: 0,
    cacheTime: 0,
    retry: 2,
  });
};
